import { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    getCargaJauregui, getResultadosJauregui, getBloquesJauregui,
    guardarCargaJauregui, quitarCargaJauregui,
} from '../../services/jaureguiService';
import { usuarioService } from '../../services/usuarioService';

/** yyyy-MM-dd → dd/mm/yyyy, sin pasar por Date (evita el corrimiento de huso horario). */
function formatearFechaCorta(fechaISO) {
    if (!fechaISO) return '';
    const [y, m, d] = String(fechaISO).split('T')[0].split('-');
    return `${d}/${m}/${y}`;
}

/**
 * Semáforo de un plazo: verde en plazo, rojo vencido. Los días los calcula el backend (con el
 * plazo de cada carpeta), así que acá solo se pinta — a diferencia de la tarjeta vieja de Reajuste,
 * que los calculaba en el frontend y podía no coincidir con el listado.
 */
function SemaforoPlazo({ titulo, fecha, diasRestantes, plazoDias, textoSinFecha }) {
    if (!fecha) {
        return <div className="text-muted small">{textoSinFecha}</div>;
    }
    const vencido = diasRestantes != null && diasRestantes < 0;
    return (
        <div className={`alert ${vencido ? 'alert-danger' : 'alert-success'} border-0 rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-2 mb-0 py-2`}>
            <span>
                <i className={`bi ${vencido ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-2`}></i>
                {titulo} <strong>{formatearFechaCorta(fecha)}</strong>
                {plazoDias ? <span className="text-muted ms-2 small">({plazoDias} días)</span> : null}
            </span>
            {diasRestantes != null && (
                <span className="fw-semibold">
                    {diasRestantes < 0
                        ? `Demorado hace ${Math.abs(diasRestantes)} ${Math.abs(diasRestantes) === 1 ? 'día' : 'días'}`
                        : diasRestantes === 0
                            ? 'Vence hoy'
                            : `En plazo — quedan ${diasRestantes} ${diasRestantes === 1 ? 'día' : 'días'}`}
                </span>
            )}
        </div>
    );
}

/**
 * Tarjeta "Carga en Jauregui", compartida por las fichas de Reajuste, Jubilación, RTI y
 * Reconocimiento (sumarla a otro tipo es una línea más).
 *
 * QUÉ ES
 * El trabajo previo al reclamo: se toma la vista del expediente, se pasa la carpeta a alguien para
 * cargar el cálculo (30 o 60 días) y según el resultado se llama al cliente. Es la contracara en la
 * ficha del listado "Para cargar Jauregui", que es solo lectura: todo se edita acá.
 *
 * REEMPLAZA A `TarjetaLlamadoReajuste`
 * El llamado al cliente era una tarjeta aparte al pie de la card de Estado, con su propio plazo de
 * 30 días calculado en el frontend. Ahora los dos plazos viven juntos y los calcula el backend, así
 * que la ficha y el listado no pueden mostrar cosas distintas.
 *
 * SIEMPRE ES UNA CARD PROPIA
 * La primera versión se dibujaba con un `border-top` dentro de la card de Estado, y quedaba
 * pegada al resto: en Reajuste se leía como una sección más del estado, y en Jubilación, RTI y
 * Reconocimiento —donde no hay una card que la contenga— quedaba suelta entre bloques. Ahora es
 * una card con su propio título, igual que las demás tarjetas de la ficha, así se lee como lo que
 * es: un trabajo aparte del ciclo de estados.
 *
 * @param idTramite    el trámite.
 * @param onCambio     se llama después de guardar, para que la ficha recargue lo que muestre el dato.
 */
export default function TarjetaCargaJauregui({ idTramite, onCambio }) {
    const [carga, setCarga] = useState(null);
    const [fechaPaseSugerida, setFechaPaseSugerida] = useState(null);
    const [cargando, setCargando] = useState(true);

    const [resultados, setResultados] = useState([]);
    const [bloques, setBloques] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [form, setForm] = useState({});
    const [guardando, setGuardando] = useState(false);

    const recargar = useCallback(async () => {
        if (!idTramite) return;
        setCargando(true);
        try {
            const data = await getCargaJauregui(idTramite);
            // Sin carpeta el backend puede devolver solo la fecha sugerida (el turno de VISTA):
            // se distingue por la ausencia de idTramite en la respuesta.
            if (data && data.idTramite) {
                setCarga(data);
                setFechaPaseSugerida(null);
            } else {
                setCarga(null);
                setFechaPaseSugerida(data?.fechaPaseSugerida || null);
            }
        } catch (err) {
            console.error('Error al cargar la carga en Jauregui:', err);
            setCarga(null);
        } finally {
            setCargando(false);
        }
    }, [idTramite]);

    useEffect(() => { recargar(); }, [recargar]);

    // Los catálogos se piden una sola vez, al abrir el modal: no hacen falta para mostrar la
    // tarjeta y son tres pedidos que no tienen por qué salir en cada ficha que se abre.
    useEffect(() => {
        if (!modalAbierto || resultados.length > 0) return;
        getResultadosJauregui().then(setResultados).catch(() => setResultados([]));
        getBloquesJauregui().then(setBloques).catch(() => setBloques([]));
        // getParaSeleccion y no getAll: es la lista mínima pensada para selectores operativos, que
        // cualquier rol autenticado puede ver. getAll exige el permiso de administrar usuarios, así
        // que el selector habría salido vacío para quien justamente usa esta tarjeta.
        usuarioService.getParaSeleccion()
            .then((resp) => {
                const data = resp?.data;
                setUsuarios(Array.isArray(data) ? data : (data?.content || []));
            })
            .catch(() => setUsuarios([]));
    }, [modalAbierto, resultados.length]);

    const abrirModal = () => {
        setForm({
            fechaPase: carga?.fechaPase || fechaPaseSugerida || '',
            plazoDias: String(carga?.plazoDias || 30),
            idUsuarioCarga: carga?.idUsuarioCarga ? String(carga.idUsuarioCarga) : '',
            resultado: carga?.resultado || '',
            fechaResultado: carga?.fechaResultado || '',
            bloqueManual: carga?.bloqueEsManual ? (carga?.bloque || '') : '',
            observacion: carga?.observacion || '',
            fechaLlamado: carga?.fechaLlamado || '',
            plazoLlamadoDias: String(carga?.plazoLlamadoDias || 30),
        });
        setModalAbierto(true);
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            // Los "limpiar" son explícitos: el backend interpreta un null como "no lo toques", así
            // que vaciar un campo en el formulario tiene que viajar como un borrado.
            const dto = {
                fechaPase: form.fechaPase || null,
                plazoDias: Number(form.plazoDias) || 30,
                observacion: form.observacion ?? '',
                plazoLlamadoDias: Number(form.plazoLlamadoDias) || 30,
            };
            if (form.idUsuarioCarga) {
                dto.idUsuarioCarga = Number(form.idUsuarioCarga);
            } else {
                dto.limpiarUsuarioCarga = true;
            }
            if (form.resultado) {
                dto.resultado = form.resultado;
                dto.fechaResultado = form.fechaResultado || null;
            } else {
                dto.limpiarResultado = true;
            }
            if (form.bloqueManual) {
                dto.bloqueManual = form.bloqueManual;
            } else {
                dto.limpiarBloqueManual = true;
            }
            if (form.fechaLlamado) {
                dto.fechaLlamado = form.fechaLlamado;
            } else {
                dto.limpiarFechaLlamado = true;
            }

            const actualizado = await guardarCargaJauregui(idTramite, dto);
            setCarga(actualizado);
            setModalAbierto(false);
            toast.success('Carga en Jauregui guardada');
            onCambio?.(actualizado);
        } catch (err) {
            console.error('Error al guardar la carga en Jauregui:', err);
            toast.error(err.response?.data || 'No se pudo guardar la carga en Jauregui');
        } finally {
            setGuardando(false);
        }
    };

    const quitar = async () => {
        setGuardando(true);
        try {
            await quitarCargaJauregui(idTramite);
            setCarga(null);
            setModalAbierto(false);
            toast.info('La carpeta salió del listado');
            await recargar();
            onCambio?.(null);
        } catch (err) {
            console.error('Error al quitar la carpeta del listado:', err);
            toast.error('No se pudo quitar la carpeta del listado');
        } finally {
            setGuardando(false);
        }
    };

    if (cargando) {
        return null;
    }

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-3">

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h4 className="fw-bold mb-0 text-primary">
                    <i className="bi bi-calculator me-2"></i>Carga en Jauregui
                    {carga?.prioridad && (
                        <span className="badge rounded-pill bg-warning text-dark ms-2 align-middle" style={{ fontSize: '0.7rem' }}>
                            <i className="bi bi-star-fill me-1"></i>Prioridad
                        </span>
                    )}
                </h4>
                <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={abrirModal}>
                    <i className="bi bi-pencil me-1"></i>
                    {carga ? 'Editar' : 'Pasar a cargar'}
                </button>
            </div>

            {!carga ? (
                <div className="text-muted small">
                    Esta carpeta todavía no se pasó a cargar.
                    {fechaPaseSugerida && (
                        <> Se tomó vista el <strong>{formatearFechaCorta(fechaPaseSugerida)}</strong>, así que
                        al pasarla esa fecha se propone sola.</>
                    )}
                </div>
            ) : (
                <>
                    <div className="row g-2 small mb-3">
                        <div className="col-md-4">
                            <span className="text-muted">Bloque:</span>{' '}
                            <strong>{carga.bloqueEtiqueta || '—'}</strong>
                            {carga.bloqueEsManual && <span className="text-muted ms-1">(a mano)</span>}
                        </div>
                        <div className="col-md-4">
                            <span className="text-muted">¿Quién carga?:</span>{' '}
                            <strong>{carga.usuarioCarga || 'Sin asignar'}</strong>
                        </div>
                        <div className="col-md-4">
                            <span className="text-muted">Resultado:</span>{' '}
                            <strong>{carga.resultadoEtiqueta || 'Sin cargar'}</strong>
                            {carga.fechaResultado && (
                                <span className="text-muted ms-1">({formatearFechaCorta(carga.fechaResultado)})</span>
                            )}
                        </div>
                        {carga.observacion && (
                            <div className="col-12">
                                <span className="text-muted">Observación:</span> {carga.observacion}
                            </div>
                        )}
                    </div>

                    <div className="mb-2">
                        <SemaforoPlazo
                            titulo="Pasada a cargar el"
                            fecha={carga.fechaPase}
                            diasRestantes={carga.diasRestantesCarga}
                            plazoDias={carga.plazoDias}
                            textoSinFecha="Falta la fecha de la vista / pase: sin ella no corre el plazo de carga."
                        />
                    </div>

                    <SemaforoPlazo
                        titulo="Se llamó al cliente el"
                        fecha={carga.fechaLlamado}
                        diasRestantes={carga.diasRestantesLlamado}
                        plazoDias={carga.plazoLlamadoDias}
                        textoSinFecha="Todavía no se registró el llamado al cliente (aviso de turno)."
                    />
                </>
            )}

            {/* Tres filas de campos dentro de un modal `lg`: entra entero, sin scroll interno.
                Sin `scrollable` a propósito — con esa prop el cuerpo se queda con su propia barra
                y el diálogo se pega al navbar; sin ella, si alguna vez no entrara, scrollea la
                página y el modal sigue centrado.

                El tamaño va por la prop `size` de Bootstrap y NUNCA por CSS propio. Este modal se
                veía angosto y corrido a la izquierda por un `.modal-content` global que declaraba
                CrearEventoModal.css y le ganaba a Bootstrap; se arregló renombrando aquella clase,
                no agregando CSS acá. */}
            <Modal show={modalAbierto} onHide={() => !guardando && setModalAbierto(false)}
                centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-calculator me-2 text-primary"></i>Carga en Jauregui
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Tres filas de 12 columnas: pase/plazo/quién carga · resultado/fecha/bloque ·
                        llamado/plazo/observación. Agrupados por tema y no por tipo de campo, que es
                        como se completan: primero se pasa la carpeta, después vuelve el cálculo y al
                        final se llama al cliente. */}
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label fw-semibold">Fecha de la vista / pase</label>
                            <input type="date" className="form-control" value={form.fechaPase || ''}
                                onChange={(e) => setForm(p => ({ ...p, fechaPase: e.target.value }))} />
                            <small className="text-muted">Desde acá corre el plazo de carga.</small>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-semibold">Plazo de carga</label>
                            <select className="form-select" value={form.plazoDias || '30'}
                                onChange={(e) => setForm(p => ({ ...p, plazoDias: e.target.value }))}>
                                <option value="30">30 días</option>
                                <option value="60">60 días</option>
                            </select>
                        </div>
                        <div className="col-md-5">
                            <label className="form-label fw-semibold">¿Quién carga?</label>
                            <select className="form-select" value={form.idUsuarioCarga || ''}
                                onChange={(e) => setForm(p => ({ ...p, idUsuarioCarga: e.target.value }))}>
                                <option value="">Sin asignar</option>
                                {usuarios.map((u) => (
                                    <option key={u.id} value={u.id}>{u.nombre || u.email}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-5">
                            <label className="form-label fw-semibold">Resultado del cálculo</label>
                            <select className="form-select" value={form.resultado || ''}
                                onChange={(e) => setForm(p => ({ ...p, resultado: e.target.value }))}>
                                <option value="">Todavía sin cargar</option>
                                {resultados.map((r) => (
                                    <option key={r.valor} value={r.valor}>{r.etiqueta}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-semibold">Fecha del resultado</label>
                            <input type="date" className="form-control" value={form.fechaResultado || ''}
                                disabled={!form.resultado}
                                onChange={(e) => setForm(p => ({ ...p, fechaResultado: e.target.value }))} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-semibold">Bloque</label>
                            <select className="form-select" value={form.bloqueManual || ''}
                                onChange={(e) => setForm(p => ({ ...p, bloqueManual: e.target.value }))}>
                                <option value="">Automático (según el resultado)</option>
                                {bloques.map((b) => (
                                    <option key={b.valor} value={b.valor}>{b.etiqueta}</option>
                                ))}
                            </select>
                            <small className="text-muted">Si elegís uno, no se vuelve a mover solo.</small>
                        </div>

                        <div className="col-md-4">
                            <label className="form-label fw-semibold">Fecha del llamado al cliente</label>
                            <input type="date" className="form-control" value={form.fechaLlamado || ''}
                                onChange={(e) => setForm(p => ({ ...p, fechaLlamado: e.target.value }))} />
                            <small className="text-muted">Aviso de que se le va a sacar turno en ANSES.</small>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-semibold">Plazo del turno</label>
                            <select className="form-select" value={form.plazoLlamadoDias || '30'}
                                onChange={(e) => setForm(p => ({ ...p, plazoLlamadoDias: e.target.value }))}>
                                <option value="30">30 días</option>
                                <option value="60">60 días</option>
                            </select>
                        </div>
                        <div className="col-md-5">
                            <label className="form-label fw-semibold">Observación</label>
                            <textarea className="form-control" rows={2} maxLength={500}
                                value={form.observacion || ''}
                                onChange={(e) => setForm(p => ({ ...p, observacion: e.target.value }))} />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    {carga ? (
                        <button className="btn btn-outline-danger rounded-pill px-3" onClick={quitar} disabled={guardando}>
                            <i className="bi bi-x-circle me-1"></i>Sacar del listado
                        </button>
                    ) : <span />}
                    <div className="d-flex gap-2">
                        <button className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => setModalAbierto(false)} disabled={guardando}>
                            Cancelar
                        </button>
                        <button className="btn btn-primary rounded-pill px-4" onClick={guardar} disabled={guardando}>
                            {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : 'Guardar'}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
